import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';

import { ProfileContext } from '../contexts/ProfileContext';
import { getRoles, postRoles, storeStats, getVIPRoles, fetchProjectInfo } from '../service';
import { getSortedList, getTranslation } from '../utils';
import SelectionCard from '../components/ui/selectionCard';
import Loader from '../components/common/Loader';
import { CaretLeftIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { Spinner } from '../components/common';
import Button from '../components/ui/button';

export default function PgRoles() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [profile, setProfileData] = useContext(ProfileContext);
  const { masterData: { translationPrefix, customConfigurations: { colors } = {} } = {} } = profile;

  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vipRoles, setVipRoles] = useState([]);
  const [rolesFiltered, setRolesFiltered] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    getPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getChampionProject(response, projectId) {
    const types =
      response?.portalDetails?.externalProjectTypes ||
      response?.externalProjectTypes || // fallback if file shape differs
      {};

    for (const [key, cfg] of Object.entries(types)) {
      if (cfg && cfg.projectId === projectId) {
        return {
          externalProjectTypeKey: key,
          project: cfg
        };
      }
    }
    return null;
  }

  useEffect(() => {
    const { e, n, f, t } = profile.onboardingData?.vipConfig || {};
    if (!profile?.loaded || !e || !n || !f || !t) return;
    fetchVipRoles();
  }, []);

  const fetchVipRoles = async () => {
    try {
      const { data: response } = await fetchProjectInfo(
        profile.onboardingData?.vipConfig?.org,
        profile.onboardingData?.vipConfig?.team
      );

      const {
        project: {
          vipConfig: { serverUrl }
        }
      } = getChampionProject(response, profile.onboardingData?.vipConfig.orgPrefix);
      const { s, n, f, t } = profile.onboardingData?.vipConfig || {};
      const {
        data: { data = [] }
      } = await getVIPRoles(serverUrl, {
        s,
        n,
        f,
        t,
        lang: i18n.language
      });

      setVipRoles(data.sort((a, b) => b.relevance - a.relevance));
    } catch (e) {
      console.log('ERROR', e);
    }
  };

  useEffect(() => {
    let resultRoles = [];

    if (rolesFiltered) return;

    if (roles.length > 0 && vipRoles.length > 0) {
      for (let i = 0; resultRoles.length < 8 && i < roles.length; i++) {
        const vipRole = vipRoles[i];
        if (!isEmpty(vipRole)) {
          const orgRole = roles.find(
            (orgRole) =>
              orgRole?.[`vip_id_${i18n.language}`] === vipRole.id ||
              orgRole?.alternate_vip_ids?.[i18n.language]?.includes(vipRole.id)
          );
          if (orgRole) {
            resultRoles.push({ ...orgRole });
          }
        }
      }
      setRoles([...resultRoles]);
      setRolesFiltered(true);
    }
  }, [roles, vipRoles, rolesFiltered]);

  useEffect(() => {
    // Pre-select existing roles from profile
    if (profile?.roles && roles.length > 0) {
      const preSelected = roles.filter(({ _id }) => profile.roles.includes(_id));
      setSelectedRoles(preSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.roles, roles]);

  const getPageData = async () => {
    try {
      if (!profile?.loaded) return;
      const {
        data: { data: rolesData }
      } = await getRoles(profile.masterOrganization);

      const sortedRoles = getSortedList(rolesData, i18n.language);
      setRoles(sortedRoles);
    } catch (e) {
      console.log('ERROR', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (item) => {
    const isSelected = selectedRoles.find((r) => r._id === item._id);

    if (isSelected) {
      // Remove from selection
      setSelectedRoles(selectedRoles.filter((r) => r._id !== item._id));
    } else {
      // Add to selection (if not at max)
      if (selectedRoles.length < 3) {
        setSelectedRoles([...selectedRoles, item]);
      }
    }
  };

  const handleNext = async () => {
    if (selectedRoles.length !== 3) {
      return;
    }

    setSaving(true);

    try {
      const filteredRoles = selectedRoles.map(({ _id }) => _id);
      const body = { roles: filteredRoles };

      await postRoles(body);

      // Store stats for roles
      try {
        for (let selectedRole of selectedRoles) {
          await storeStats({
            application: 'champion',
            event: 'champion_chosen_role',
            orgPrefix: translationPrefix,
            pageURL: window?.location?.pathname,
            pageTitle: document?.title,
            championID: profile?._id,
            championName: `${profile?.firstName} ${profile?.familyName}`,
            roleName: selectedRole[`name_${i18n.language}`]
          });
        }
      } catch (error) {
        console.error('Error in storing stats for selected roles');
      }

      let updatedProfile = {
        ...profile,
        roles: filteredRoles,
        isUpdatedForPDF: true,
        validationReduested: false
      };

      // Update videos for role if it exists
      if (profile.videos?.videos?.length) {
        const updatedVideos = [...profile.videos.videos];
        // Check second video for role
        if (updatedVideos[1]?.roleId?._id && !filteredRoles.includes(updatedVideos[1].roleId._id)) {
          updatedVideos[1] = { ...updatedVideos[1] };
          delete updatedVideos[1].roleId;
        }

        updatedProfile.videos = {
          ...profile.videos,
          videos: updatedVideos
        };
      }

      setProfileData(updatedProfile);
      navigate('/my-profile');
    } catch (error) {
      toast.error('Some error occurred with updating the roles!');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/my-profile');
  };

  const displayedRoles =
    !rolesFiltered && searchQuery
      ? roles.filter((item) =>
          item[`name_${i18n.language}`]?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : roles;

  if (loading) return <Loader />;

  return (
    <>
      {/* Scrollable Body */}
      <div className="fixed top-24 bottom-20 left-0 right-0 overflow-y-auto">
        <div className="px-6 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Back Button and Heading */}
            <div className="relative mb-2">
              {/* Back Button - Left Aligned */}
              <button
                onClick={handleBack}
                style={{ backgroundColor: colors?.primary || '#2B4B8C' }}
                className="absolute left-0 top-1/4 -translate-y-1/2 text-white p-1.5 rounded-lg hover:opacity-90 transition-opacity">
                <CaretLeftIcon size={16} />
              </button>

              {/* Heading - Centered */}
              <h1
                style={{ color: colors?.primary || '#2B4B8C' }}
                className="text-3xl font-bold text-center"
                dangerouslySetInnerHTML={{
                  __html: getTranslation(t, 'rolesPage.title', translationPrefix)
                }}
              />
            </div>

            {/* Description */}
            <p className="text-gray-600 my-8 text-center text-lg max-w-md mx-auto">
              <span
                dangerouslySetInnerHTML={{
                  __html: getTranslation(t, 'rolesPage.description', translationPrefix)
                }}
              />
            </p>

            {!rolesFiltered && (
              <div className="flex gap-3 w-full items-center mb-6">
                <MagnifyingGlassIcon size={20} className="text-gray-500 shrink-0" />
                <input
                  className="bg-gray-200 text-black rounded-full px-4 py-3 w-full"
                  placeholder={getTranslation(t, 'searchListPlaceholder', translationPrefix)}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            )}

            {/* Selection Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {displayedRoles.map((item) => (
                <SelectionCard
                  key={item._id}
                  item={item}
                  isSelected={!!selectedRoles.find((r) => r._id === item._id)}
                  onToggle={() => toggleSelection(item)}
                  language={i18n.language}
                  primaryColor={colors?.primary}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-300 px-6 py-6 z-40">
        <div className="max-w-2xl mx-auto flex justify-center">
          <Button.ButtonRounded
            onClick={handleNext}
            disabled={selectedRoles.length !== 3 || saving}
            bgColor="secondary"
            className="w-full max-w-xs py-3">
            <Spinner isSubmitting={saving} />
            {getTranslation(t, 'rolesPage.confirmButton', translationPrefix)}
          </Button.ButtonRounded>
        </div>
      </div>

      {saving && <Loader transparent={true} />}
    </>
  );
}
